import { type IEwdRulesetWorkRestConfig, type IHosRuleset, timeSpanToMs, TranslatableDisplayStrings } from "hos-tools";
import UserHosRuleSet from "../model/UserHosRuleSet";
import LatLon from "../utils/latlon";
import { dateTimeComparator, dstCalculator } from "../utils/date";
import { getServerDate } from "./storage/date";
import { CancelablePromise as Promise } from "./cancelablePromise";
import * as logger from "./logger";
import { handleApi } from "./error";
import Api from "../services/api";
import { IShipment, Shipment } from "../model/Shipment";
import { extend } from "./vanilla";
import DutyStatusLog, { NOUSERID } from "../model/DutyStatusLog";
import { IMobilePageState } from "../store/mobile/types";
import { SyncType } from "../sync/sync.types";
import Sync from "../sync/sync";
import { DriveStore } from "../store/reducers";
import { DutyStatusLogService } from "../model/DutyStatusLogs";
import { getClonedAnnotationsForUser } from "./storeHelpers";

export type Mutable<T> = { -readonly [P in keyof T]: T[P] };

export function getMinEwdWorkMaximum (config: IEwdRulesetWorkRestConfig[] | undefined) {
    let min = Infinity;
    if (!config) {
        return min;
    }
    config.forEach(availPeriod => {
        if (!availPeriod.timePeriod) {
            return;
        }

        let value = availPeriod.workPeriod && availPeriod.workPeriod.workMaximum && availPeriod.workPeriod.workMaximum / (1000 * 60 * 60);
        if (value !== undefined && value < min) {
            min = value;
        }
    });
    return min;
}

export function convertMinutesToTimeString(durationInMinutes: number): string {
    let hours = Math.floor(durationInMinutes / 60);
    let minutes = Math.floor(durationInMinutes - hours * 60);
    let addZero = value => {
        let v = value.toString();
        if (v.length === 1) {
            v = `0${ v }`;
        }
        return v;
    };

    if (minutes === 60) {
        hours++;
        minutes = 0;
    }

    return `${ addZero(hours) }:${ addZero(minutes) }`;
}

export function timeStringToLll(timeStr: string) {
    let [hour, min] = timeStr.split(":");
    let partOfDay = "";

    let hourInt = parseInt(hour);
    if (hourInt >= 12) {
        partOfDay = "PM";
        if (hourInt > 12) {
            hourInt -= 12;
        }
    } else {
        partOfDay = "AM";
    }

    return `${ hourInt }:${ min } ${ partOfDay }`;
}

export function isVersionGreater(a: string, b: string): boolean {
    let aVersion = a.split(".").map(v => parseInt(v, 10));
    let bVersion = b.split(".").map(v => parseInt(v, 10));

    if (aVersion && aVersion.length && bVersion && bVersion.length) {
        let separations = Math.max(aVersion.length, bVersion.length);
        for (let i = 0; i < separations; i++) {
            let aVersionSegment = aVersion[i] || 0;
            let bVersionSegment = bVersion[i] || 0;

            if (aVersionSegment > bVersionSegment) {
                return true;
            } else if (aVersionSegment < bVersionSegment) {
                return false;
            }
        }
    }
    return false;
}

/**
* Calculates the coordinate speed from two points over time
* Returns a result in metres.
*/
export function calculateCoordinateSpeed(lat1: number, lng1: number, dateOrTimestamp1: Date | number, lat2: number, lng2: number, dateOrTimestamp2: Date | number): number {
    let distance = new LatLon(lat1, lng1).distanceTo(new LatLon(lat2, lng2)); // km
    let time1 = new Date(dateOrTimestamp1 as number).getTime();
    let time2 = new Date(dateOrTimestamp2 as number).getTime();

    return (distance * 1000) / ((time2 - time1) / 1000);
}

/**
 * Calculates the absolute difference between two headings
 */
export function calculateHeadingDifference(heading1: number | null, heading2: number | null): number | null {
    if (heading1 === null || heading2 === null) {
        return null;
    }
    let phi = Math.abs(heading2 - heading1) % 360;
    return phi > 180 ? 360 - phi : phi;
}

const defaultEldProvider = "Geotab";
const eldProviderByDatabase = {
    "sunbelt_rentals": "Sunbelt"
};
const eldProviderByWhitelabel: {[whitelabel in TWhiteLabel]?: string} = {
    "transflo": "Transflo",
    // Spireon run it in a completely different application (Spireon FleetLocate) which is not Geotab Drive, so we don't want us to get in trouble for that.
    "spireon": "", // Invalid
    "FMCSATest": "FMCSATest",
    "smartdrive": "SmartDrive",
    "eleos": "Eleos",
    "lytx": "Lytx",
    "shell": "Shell"
};

export function getEldProviderName(database: string, whitelabel: string | null, isCanadaEmail: boolean): string {
    if (eldProviderByDatabase.hasOwnProperty(database)) {
        return eldProviderByDatabase[database];
    }
    if (whitelabel && eldProviderByWhitelabel.hasOwnProperty(whitelabel)) {
        return eldProviderByWhitelabel[whitelabel];
    }

    if (!isCanadaEmail && database === "geotabdemo") {
        return "FMCSATest";
    }

    return defaultEldProvider;
}

/**
 * We can force enabling the test transfer by setting ENABLE_ELD_TEST_TRANSFER to true
 */
export function isEldTestTransferEnabled(): boolean {
    return (window.DRIVE_DEBUG || {}).ENABLE_ELD_TEST_TRANSFER || false;
}

export function isEldTransferValidateOnly(): boolean {
    return (window.DRIVE_DEBUG || {}).ELD_TRANSFER_VALIDATE_ONLY || false;
}

export function goBack(): void {
    if (window.history.state && window.history.state.openedDialogCount) {
        window.history.go(-1 - window.history.state.openedDialogCount);
    } else {
        window.history.back();
    }
}

export function getEventsUrl() {
    return "notifications/events";
}

export function getKeys(obj: any): string[] {
    if (obj !== null && obj !== undefined) {
        return Object.keys(obj);
    } else {
        return [];
    }
}

export function getLatestRuleSet(userHosRuleSets: UserHosRuleSet[]): UserHosRuleSet | undefined {
    let maxDatetime = 0;
    let latestRuleset: UserHosRuleSet | undefined = undefined;
    for (let i = 0; i < userHosRuleSets.length; i++) {
        let ruleSetDatetime: number = new Date(userHosRuleSets[i].dateTime).getTime();
        if (maxDatetime === 0 || ruleSetDatetime > maxDatetime) {
            maxDatetime = ruleSetDatetime;
            latestRuleset = userHosRuleSets[i];
        }
    }
    return latestRuleset;
}

export function getLatestRuleSetsNames(userHosRuleSets: UserHosRuleSet[], top: number, splitSleeperRulesetIndex: String | undefined): String[] {
    userHosRuleSets.sort(dateTimeComparator(false));
    let userHosRulesetNames = userHosRuleSets.map(ruleSet => {
        return ruleSet.hosRuleSet.id.replace("HosRuleSet", "");
    });

    let unique = userHosRulesetNames.filter(function (item, index, arr) {
        return index === arr.indexOf(item) && item !== splitSleeperRulesetIndex;
    });

    return unique.slice(0, top);
}

export interface UntranslatedLogging {
    untranslatedString: string;
    page: string | undefined;
    pageState: DeepReadonlyObject<IMobilePageState>;
    version: string | undefined;
    source: string;
}

export enum TranslationSource {
    API = "API",
    HELPERS = "HELPERS",
    REACT_MODULE = "REACT_MODULE"
}

export function translatedStringExists(id: string, translatedStrings: { [key: string]: string }) {
    return (translatedStrings && translatedStrings.hasOwnProperty(id));
}

export function getTranslatedString(untranslated: string, translatedStrings: { [key: string]: string }, language?: string, returnEmptyStringWhenMissing = false): string {
    if (!untranslated) {
        return "";
    }

    if (language && language === "en") {
        return untranslated;
    }

    if (translatedStringExists(untranslated, translatedStrings)) {
        return translatedStrings[untranslated];
    }

    return returnEmptyStringWhenMissing ? "" : `**${ untranslated }`;
}

export function getSplitSleeperRuleSetName(rules: IIdDictionary<IHosRuleset>, currentRuleSetIndex: string): String | undefined {
    if (currentRuleSetIndex.endsWith("Sleeper")) {
        return currentRuleSetIndex;
    }
    let splitSleeperRule = currentRuleSetIndex + "Sleeper";
    let ruleIndexes = Object.keys(rules);
    let sleeperRuleset = ruleIndexes?.find(ruleset => ruleset === splitSleeperRule);
    return sleeperRuleset;
}

export function isValidOdometer(odometer: string) {
    return (!odometer || !Number(odometer) || Number(odometer) <= 0 || (typeof odometer === "string"
    && Number.isNaN(Number(odometer)))) ? false : true;
}

export function getCurrentTimeWithUserTimezoneOffset(): Date {
    return dstCalculator.toLocalDateTime(getServerDate());
}

const MINUTES_SECONDS = 60;
const HOURS_SECONDS = 60 * MINUTES_SECONDS;
const DAY_SECONDS = 24 * HOURS_SECONDS;
const fullTimeSpanStrFromSeconds = function (totalSeconds: number, shortFormat = false, translate: (s: string) => string) {
    const days = Math.floor(totalSeconds / DAY_SECONDS);
    const hours = Math.floor((totalSeconds - days * DAY_SECONDS) / HOURS_SECONDS);
    const minutes = Math.floor((totalSeconds - days * DAY_SECONDS - hours * HOURS_SECONDS) / MINUTES_SECONDS);
    const seconds = Math.floor(totalSeconds - days * DAY_SECONDS - hours * HOURS_SECONDS - minutes * MINUTES_SECONDS);
    const displayStrings: string[] = [];

    if (days > 0) {
        displayStrings.push(days + translate("days"));
    }
    if (displayStrings.length > 0 || hours > 0) {
        displayStrings.push(hours + translate("hours"));
    }
    if ((!shortFormat || days === 0) && (displayStrings.length > 0 || minutes > 0)) {
        displayStrings.push(minutes + translate("{minutes}m").replace("{minutes}", ""));
    }
    if ((!shortFormat && displayStrings.length === 0) || (days === 0 && hours === 0)) {
        displayStrings.push(seconds + translate("seconds"));
    }

    return displayStrings.join(" ") || "0";
};

export function getDisplayTimeSpan (
    timespan: string,
    shortFormat = false,
    translatedDaysUnit: string = TranslatableDisplayStrings.DAYS_UNIT,
    translatedHoursUnit: string = TranslatableDisplayStrings.HOUR_UNIT,
    translatedMinutesUnit: string = TranslatableDisplayStrings.MINUTES_STRING,
    translatedSecondsUnit: string = TranslatableDisplayStrings.SECONDS_UNIT
) {
    const local: { [key: string]: string } = { "days": translatedDaysUnit, "hours": translatedHoursUnit, "{minutes}m": translatedMinutesUnit, "seconds": translatedSecondsUnit };
    return fullTimeSpanStrFromSeconds(timeSpanToMs(timespan) / 1000, shortFormat, (key: string) => local[key]);
}

export function cloneStorageToDictionary<T>(logs: DeepReadonlyObject<IIdDictionary<T>>): IIdDictionary<T> {
    let cloneDictionary: IIdDictionary<T> = {};
    Object.keys(logs).forEach(key => {
        if (logs) {
            cloneDictionary[key] = extend(true, {}, logs[key]);
        }
    });
    return cloneDictionary;
}

/**
 * Converts a nested object to a string representation in the format "key:value".
 * This function iterates through the object's properties recursively and concatenates
 * them into a string, including nested properties with their full key paths.
 * Note: Use this function only when JSON.stringify format is not expected.
 *
 * @param obj The object to be converted to a string representation.
 * @returns A string representation of the object's properties in "key:value" format.
 */
export function objectToString<T>(obj: T): string {
    const properties: string[] = [];

    /**
     * Recursively iterates through the nested object's properties.
     * @param nestedObj The nested object to be iterated.
     * @param parentKey The key path of the parent object (used for nesting).
     */
    function stringifyNestedObject(nestedObj: any, parentKey = "") {
        for (const key in nestedObj) {
            if (nestedObj.hasOwnProperty(key)) {
                const value = nestedObj[key];
                const fullKey = parentKey ? `${ parentKey }_${ key }` : key;

                if (value && typeof value === "object") {
                    stringifyNestedObject(value, fullKey);
                } else {
                    properties.push(`${ fullKey }:${ value }`);
                }
            }
        }
    }

    stringifyNestedObject(obj);
    return properties.join(",");
}

/**
 * Compares two version strings.
 *
 * @param version1 - The first version string (if undefined/invalid, treated as oldest version)
 * @param version2 - The second version string (if undefined/invalid, treated as oldest version)
 *
 * @returns A number indicating the result of the comparison:
 *  - -1 if the first version is less than the second version,
 *  - 1 if the first version is greater than the second version,
 *  - 0 if the versions are equal.
 */
export function versionComparator(version1: string | undefined, version2: string | undefined): number {
    // Handle undefined or non-string inputs
    if (typeof version1 !== "string" && typeof version2 !== "string") {
        return 0;
    }
    if (typeof version1 !== "string") {
        return -1;
    }
    if (typeof version2 !== "string") {
        return 1;
    }

    // Split the version strings into parts using the "." and "_" characters as separators
    const parts1 = version1.split(/[._]/);
    const parts2 = version2.split(/[._]/);
    const maxLength = Math.max(parts1.length, parts2.length);

    for (let i = 0; i < maxLength; i++) {
        // If a part exists in the version, parse it as an integer, otherwise set to null
        const part1 = i < parts1.length ? parseInt(parts1[i], 10) : null;
        const part2 = i < parts2.length ? parseInt(parts2[i], 10) : null;

        // If both parts exist and are integers
        if (part1 !== null && part2 !== null) {
            if (part1 < part2) {
                return -1;
            } else if (part1 > part2) {
                return 1;
            }
        } else if (part1 !== null) {
            return 1;
        } else if (part2 !== null) {
            return -1;
        }
    }
    // If after checking all parts no difference was found, return 0 (versions are equal)
    return 0;
}

export function convertStorageRulesetsToDictionary(logs) {
    let ruleSetsDictionary: IIdDictionary<UserHosRuleSet> = {};
    if (logs) {
        Object.keys(logs).forEach(key => {
            if (logs) {
                ruleSetsDictionary[key] = new UserHosRuleSet(logs[key]);
            }
        });
    }
    return ruleSetsDictionary;
}

export function convertStorageShipmentToDictionary(logs: DeepReadonlyObject<IIdDictionary<IShipment>> | undefined | "") {
    let shipmentsDictionary: IIdDictionary<IShipment> = {};
    if (logs) {
        Object.keys(logs).forEach(key => {
            if (logs) {
                shipmentsDictionary[key] = new Shipment(logs[key]);
            }
        });
    }
    return shipmentsDictionary;
}

function getKnownDeviceId(id: string | null): string | null {
    return id && id !== "NoDeviceId" ? id : null;
}

export function getStatusData(api: Api, requestedDeviceId: string | null, fromDate: Date, toDate: Date, diagnosticId: TDiagnosticId, resultsLimit = 1) {
    return new Promise<IStatusData[]>((resolve, reject) => {
        let deviceId = getKnownDeviceId(requestedDeviceId);
        if (deviceId) {
            api.call("Get", {
                typeName: "StatusData",
                search: {
                    deviceSearch: { id: deviceId },
                    diagnosticSearch: { id: diagnosticId },
                    fromDate: fromDate,
                    toDate: toDate
                },
                resultsLimit: resultsLimit
            }, (result: IStatusData[]) => {
                if (result) {
                    resolve(result);
                } else {
                    logger.error(
                        logger.Source.ERROR,
                        `GetStatusData DeviceId: ${ deviceId } DiagnosticId: ${ diagnosticId } From/ToDate: ${ fromDate.toISOString() }/${ toDate.toISOString() }`
                    );
                    reject(null);
                }
            }, (err => {
                handleApi(err);
                reject(err);
            }));
        } else {
            reject(null);
        }
    });
}

export function getRawOdometer(api: Api, requestedDeviceId: string | null, dateTimes: [Date]) {
    return new Promise<IOdometerData[]>((resolve, reject) => {
        let deviceId = getKnownDeviceId(requestedDeviceId);
        if (deviceId) {
            api.call("CalculateHosOdometer", {
                deviceSearch: { id: deviceId },
                dateTimes
            }, (result: IOdometerData[]) => {
                if (result) {
                    resolve(result);
                } else {
                    logger.error(
                        logger.Source.ERROR,
                        `GetRawOdometerData DeviceId: ${ deviceId } DateTimes: ${ dateTimes.map(dt => dt.toISOString()).join(", ") }`
                    );
                    reject(null);
                }
            }, (err => {
                handleApi(err);
                reject(err);
            }));
        } else {
            reject(null);
        }
    });
}

export function getDeviceStatusInfo(api: Api, deviceId: string): Promise<IDeviceStatusInfo[] | null> {
    return new Promise<IDeviceStatusInfo[]>((resolve, reject) => {
        if (deviceId) {
            api.call("Get", {
                typeName: "DeviceStatusInfo",
                search: {
                    deviceSearch: {id: deviceId}
                },
                propertySelector: {
                    fields: ["exceptionEvents", "statusData"],
                    isIncluded: false
                },
                resultsLimit: 1
            }, (result: IDeviceStatusInfo[]) => {
                if (result) {
                    resolve(result);
                } else {
                    logger.error(logger.Source.ERROR, `GetDeviceStatusInfo Device Id: ${ deviceId }`);
                    reject(result);
                }
            }, (err => {
                handleApi(err);
                reject(err);
            }));
        } else {
            reject(null);
        }
    });
}

export function getLastAnnotation(store: DriveStore, dutyStatusLogService: DutyStatusLogService, status: String, userID: string) {
    let logCollection = dutyStatusLogService.getHOSLogs();
    let standardLogs = dutyStatusLogService.getSortedHosLogs(logCollection.filter(hosLog => hosLog.isStandardStatusDriverLog()));
    let lastLog = dutyStatusLogService.getLatestLog(standardLogs, undefined, (hosLog: DutyStatusLog) => hosLog.status === status);
    let lastAnnotation = lastLog?.annotations?.[lastLog.annotations.length - 1];

    if (!lastAnnotation || userID === ""){
        return "";
    }

    const annotationList = getClonedAnnotationsForUser(store, userID);
    let annotation = annotationList[lastAnnotation.id] || lastAnnotation;
    return annotation.comment;
}

export function isExemptionAnnotationLengthValid(annotationText?: string){
    let exemptionAnnotation = annotationText ?? (<HTMLInputElement>document.getElementById("exemption-annotation")).value.trim();
    let errorNoticeBox = <HTMLInputElement>document.getElementById("exemption-annotation-error");
    // Always hide the box first (reset state)
    errorNoticeBox.style.display = "none";
    if (exemptionAnnotation.length < 4 || exemptionAnnotation.length > 60) {
        errorNoticeBox.style.display = "block";
        return false;
    }
    return true;
}

export function setUsingReassignApi(reassignedLogs: DutyStatusLog[], currentUser: DeepReadonlyObject<IUser> | null, isUnassign: boolean, sync: Sync, backgroundSync = false) {
    if (!currentUser?.id) {
        throw new Error("Could not assign logs due to missing user id.");
    }

    let setPromises: Promise[] = [];
    const editDate = getServerDate().toISOString();
    const newDriver = { id: isUnassign ? NOUSERID : currentUser.id };
    const originalDriverBeforeEdit = isUnassign ? currentUser.id : NOUSERID;
    reassignedLogs.forEach(log => {
        let realLog = new DutyStatusLog(log);
        realLog.editDateTime = editDate;
        realLog.driver = newDriver;
        realLog.originalDriverBeforeEdit = originalDriverBeforeEdit;
        realLog.cleanUp();
        setPromises.push(sync.dutyStatusLogs.setItem(realLog, true, false));
    });

    if (backgroundSync) {
        return Promise.all(setPromises).then(() => {
            // In this case, do not wait for the sync to finish before resolving (i.e. this is a fire-and-forget sync).
            // Use-case -- we don't want to block the UI for users in the workflow, they can continue using the app (perform
            // a DVIR, etc..) while the sync happens in the background.
            sync.synchronize(false, null, [SyncType.HOS]);
        });
    }

    return Promise.all(setPromises).then(() => sync.synchronize(false, null, [SyncType.HOS]));
}

/**
 * Takes in arrays which don't contain duplicates and compares using {@link Set}s
 * @param arrays Arrays without duplicates
 * @returns Boolean
 */
export function doArraySetsMatch(...arrays: any[][]): Boolean {
    const allEntries = Array.prototype.concat.apply([], arrays);
    const union = new Set(allEntries);
    return arrays.every(arr => union.size === arr.length);
}

export function cleanSessionAndPassword(message: string) {
    // Matches both:
    //   - key=value / key: value / key "value" style (e.g. "sessionId=abc123")
    //   - key (value) style emitted by USER_EVENT logs (e.g. "change #password (secret) ui/login")
    // The alternation tries the parenthetical form first so greedy [^,()]+ does not consume the '('.
    // [^,()]* (zero or more non-delimiter chars) before '(' generalises the separator: it handles
    // whitespace ("password (v)"), no separator ("password(v)"), and non-whitespace separators
    // ("password=(v)", "password: (v)") without breaking existing key=value matching.
    const pattern = /(sessionId|password)(?:'|")?(?:[^,()]*\([^)]*\)|[^,()]+)/ig;
    return message.replace(pattern, "$1********");
}

/**
 * Determines if the big day exemption button should be disabled
 * @param exemption16HValid - Whether the 16-hour exemption is valid/available
 * @param isAdverseWeatherApplied - Whether adverse weather conditions are currently applied
 * @param hasExemptionLogsInteractionRefactorKillSwitch - Whether the feature flag is enabled
 * @returns true if the button should be disabled, false otherwise
 */
export function isBigDayExemptionButtonDisabled(
    exemption16HValid: boolean | undefined,
    isAdverseWeatherApplied: boolean | undefined,
    hasExemptionLogsInteractionRefactorKillSwitch: boolean
): boolean {
    if (hasExemptionLogsInteractionRefactorKillSwitch) {
        return !exemption16HValid || Boolean(isAdverseWeatherApplied);
    }
    return !exemption16HValid;
}

type TDiagnosticId = "DiagnosticOdometerAdjustmentId" | "DiagnosticEngineHoursAdjustmentId" | "DiagnosticEngineRoadSpeedId";

export const intRound = function (num: number, dec: number) {
    return Math.round(num * Math.pow(10, dec)) / Math.pow(10, dec);
};

export const secondsToHours = seconds => intRound(seconds / 3600, 2);

export const hoursToSeconds = hours => hours * 3600;

export const isIdEntity = (entity): entity is IIdEntity => !!(entity && (entity as IIdEntity).id);